/**
 * Roster — tablet-first sign-in / sign-out attendance.
 *
 * Calm, fast, accountable. Big touch targets.
 *  - One row per child of the workspace.
 *  - Status pill: Present / Departed / Not yet / Resting at home.
 *  - Sign in (one tap) or Sign out (modal: pickup person required).
 *  - Daily counters at the top + admin audit log link.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import Brand from "../components/Brand";
import {
  ArrowLeft,
  Search,
  Filter,
  LogIn,
  LogOut,
  CheckCircle2,
  X,
  ScrollText,
  ChevronRight,
  Sparkles,
} from "lucide-react";

const STATUS_COLOR = {
  present: { bg: "#8A9984", fg: "#FFFFFF", label: "Present" },
  departed: { bg: "#D6CFE7", fg: "#2C2B29", label: "Headed home" },
  not_yet: { bg: "#F2EFEA", fg: "#6A6863", label: "Not yet here" },
  absent_today: { bg: "#FFB800", fg: "#1A1B2E", label: "Resting at home" },
};

const RELATIONS = [
  { id: "parent", label: "Parent" },
  { id: "grandparent", label: "Grandparent" },
  { id: "sibling", label: "Sibling" },
  { id: "aunt_uncle", label: "Aunt / Uncle" },
  { id: "family_friend", label: "Family friend" },
  { id: "sitter", label: "Sitter" },
  { id: "neighbor", label: "Neighbor" },
  { id: "other", label: "Other" },
];

function timeStr(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

export default function Roster() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [workspace, setWorkspace] = useState(null);
  const [roster, setRoster] = useState({ rows: [], counts: {} });
  const [search, setSearch] = useState("");
  const [groupFilter, setGroupFilter] = useState("all");
  const [signOutFor, setSignOutFor] = useState(null);  // child row to sign out
  const [pickupName, setPickupName] = useState("");
  const [pickupRel, setPickupRel] = useState("family_friend");
  const [pickupAuthId, setPickupAuthId] = useState("");
  const [pickupAuths, setPickupAuths] = useState([]);
  const [pickupNote, setPickupNote] = useState("");
  const [signInNote, setSignInNote] = useState("");
  const [signInFor, setSignInFor] = useState(null); // child row to sign in
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  // Find the user's primary workspace.
  useEffect(() => {
    if (!user) return;
    let alive = true;
    api.get("/workspaces").then((r) => {
      if (!alive) return;
      const list = r.data || [];
      const mine = list.find((w) => {
        if (user.role === "admin") return (w.admin_ids || []).includes(user.id);
        if (user.role === "caregiver")
          return (w.caregiver_ids || []).includes(user.id);
        return false;
      });
      setWorkspace(mine || null);
    });
    return () => {
      alive = false;
    };
  }, [user]);

  const reload = async () => {
    if (!workspace) return;
    setLoading(true);
    try {
      const r = await api.get(`/workspaces/${workspace.id}/roster`);
      setRoster(r.data || { rows: [], counts: {} });
    } catch {
      // quiet
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!workspace) return;
    reload();
    const id = setInterval(reload, 30 * 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace?.id]);

  const groups = useMemo(() => {
    const s = new Set();
    (roster.rows || []).forEach((c) => c.group_name && s.add(c.group_name));
    return ["all", ...Array.from(s)];
  }, [roster.rows]);

  const visible = useMemo(() => {
    let list = roster.rows || [];
    if (groupFilter !== "all")
      list = list.filter((c) => c.group_name === groupFilter);
    const q = search.trim().toLowerCase();
    if (q)
      list = list.filter(
        (c) =>
          c.child_name.toLowerCase().includes(q) ||
          (c.group_name || "").toLowerCase().includes(q)
      );
    return list;
  }, [roster.rows, groupFilter, search]);

  const openSignIn = (row) => {
    setSignInFor(row);
    setSignInNote("");
  };

  const confirmSignIn = async () => {
    if (!signInFor) return;
    setBusy(true);
    try {
      await api.post(
        `/workspaces/${workspace.id}/sign-in/${signInFor.child_id}`,
        { note: signInNote.trim() || null }
      );
      toast(`${signInFor.child_name} signed in.`);
      setSignInFor(null);
      await reload();
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't sign in.");
    } finally {
      setBusy(false);
    }
  };

  const openSignOut = async (row) => {
    setSignOutFor(row);
    setPickupName("");
    setPickupRel("family_friend");
    setPickupAuthId("");
    setPickupNote("");
    // Fetch trusted pickup adults for this child to suggest.
    try {
      const r = await api.get(`/children/${row.child_id}/pickup-authorizations`);
      setPickupAuths(r.data || []);
    } catch {
      setPickupAuths([]);
    }
  };

  const choosePickupAuth = (auth) => {
    setPickupAuthId(auth.id);
    setPickupName(auth.name);
    setPickupRel(auth.relation || "family_friend");
  };

  const confirmSignOut = async () => {
    if (!signOutFor) return;
    if (!pickupName.trim()) {
      toast("Please add the pickup person's name.");
      return;
    }
    setBusy(true);
    try {
      await api.post(
        `/workspaces/${workspace.id}/sign-out/${signOutFor.child_id}`,
        {
          pickup_person_name: pickupName.trim(),
          pickup_person_relation: pickupRel,
          pickup_auth_id: pickupAuthId || null,
          note: pickupNote.trim() || null,
        }
      );
      toast(`${signOutFor.child_name} signed out with ${pickupName.trim()}.`);
      setSignOutFor(null);
      await reload();
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't sign out.");
    } finally {
      setBusy(false);
    }
  };

  if (!user || (user.role !== "caregiver" && user.role !== "admin")) {
    return (
      <div className="min-h-screen bg-[#FBF9F6] flex items-center justify-center px-6">
        <p data-testid="roster-forbidden" className="font-serif italic text-[#6A6863]">
          The roster is for caregivers and admins.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="roster-screen" className="min-h-screen pb-32">
      <header className="px-5 sm:px-10 pt-6 flex items-center justify-between flex-wrap gap-4">
        <button
          type="button"
          onClick={() => navigate("/staff")}
          data-testid="roster-back"
          className="flex items-center gap-2 text-[#6A6863] hover:text-[#2C2B29] transition-colors"
        >
          <ArrowLeft size={18} strokeWidth={1.5} />
          <span className="text-sm">Live view</span>
        </button>
        <Brand size="sm" suffix={workspace?.name || "Roster"} dataTestId="brand-roster" />
      </header>

      <main className="px-5 sm:px-10 pt-6 max-w-6xl mx-auto fade-up">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <span className="eyebrow">Roster</span>
            <h1 className="mt-2 font-serif text-3xl sm:text-4xl tracking-tight">
              Today's gentle attendance.
            </h1>
            <p className="mt-2 text-sm text-[#6A6863] italic font-serif">
              Human-updated by your team. Never a camera, never a sensor.
            </p>
          </div>
          {user?.role === "admin" && workspace && (
            <button
              type="button"
              onClick={() => navigate(`/roster/log`)}
              data-testid="roster-open-log"
              className="btn-ghost !py-2 !px-4 !text-xs"
            >
              <ScrollText size={12} strokeWidth={1.5} />
              Audit log
              <ChevronRight size={12} strokeWidth={1.5} />
            </button>
          )}
        </div>

        {/* Counters */}
        <section
          data-testid="roster-counts"
          className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3"
        >
          {[
            { key: "present", label: "Present", color: "#8A9984" },
            { key: "departed", label: "Headed home", color: "#A89CC8" },
            { key: "not_yet", label: "Not yet here", color: "#9BA995" },
            { key: "absent_today", label: "Resting at home", color: "#D4A85F" },
          ].map((c) => (
            <div
              key={c.key}
              data-testid={`roster-count-${c.key}`}
              className="rounded-2xl border hairline bg-white px-4 py-4 flex items-center gap-3"
            >
              <span
                className="h-2.5 w-2.5 rounded-full"
                aria-hidden
                style={{ background: c.color }}
              />
              <div className="flex-1 min-w-0">
                <div className="font-serif text-2xl text-[#2C2B29] leading-none">
                  {roster.counts?.[c.key] ?? 0}
                </div>
                <div className="text-[10px] tracking-[0.18em] uppercase text-[#6A6863] mt-1">
                  {c.label}
                </div>
              </div>
            </div>
          ))}
        </section>

        {/* Search + filter */}
        <section className="mt-6 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 bg-white border hairline rounded-full px-4 py-2 flex-1 min-w-[200px]">
            <Search size={14} strokeWidth={1.5} className="text-[#6A6863]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search a child or group…"
              data-testid="roster-search"
              className="bg-transparent outline-none text-sm flex-1 text-[#2C2B29] placeholder:text-[#6A6863]/60"
            />
          </div>
          {groups.length > 1 && (
            <div className="flex items-center gap-2 flex-wrap" data-testid="roster-group-filter">
              <Filter size={12} strokeWidth={1.5} className="text-[#6A6863]" />
              {groups.map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => setGroupFilter(g)}
                  data-testid={`roster-group-${g === "all" ? "all" : g.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                  aria-pressed={groupFilter === g}
                  className={
                    "px-3 py-1.5 rounded-full text-xs transition-colors " +
                    (groupFilter === g
                      ? "bg-[#132619] text-white"
                      : "bg-white border hairline text-[#6A6863] hover:text-[#2C2B29]")
                  }
                >
                  {g === "all" ? "All groups" : g}
                </button>
              ))}
            </div>
          )}
        </section>

        {/* Roster list */}
        <section className="mt-6" data-testid="roster-list-section">
          {loading ? (
            <p className="font-serif italic text-[#6A6863]">A quiet moment…</p>
          ) : visible.length === 0 ? (
            <p
              data-testid="roster-empty"
              className="font-serif italic text-[#6A6863] rounded-2xl border hairline bg-white p-7"
            >
              No children match. The room is settled.
            </p>
          ) : (
            <ol data-testid="roster-list" className="space-y-2">
              {visible.map((row) => {
                const status = STATUS_COLOR[row.status];
                return (
                  <li
                    key={row.child_id}
                    data-testid={`roster-row-${row.child_id}`}
                    className="rounded-2xl border hairline bg-white p-4 flex items-center gap-4 flex-wrap"
                  >
                    <div className="h-14 w-14 rounded-full bg-[#F2EFEA] overflow-hidden shrink-0">
                      {row.portrait_url ? (
                        <img
                          src={row.portrait_url}
                          alt={row.child_name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center font-serif text-xl italic text-[#8A9984]">
                          {row.child_name[0]}
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-serif text-lg text-[#2C2B29] truncate">
                        {row.child_name}
                      </div>
                      <div className="mt-1 flex items-center gap-2 flex-wrap">
                        <span
                          className="inline-block rounded-full text-[10px] tracking-[0.18em] uppercase px-2.5 py-0.5"
                          style={{ background: status.bg, color: status.fg }}
                          data-testid={`roster-row-${row.child_id}-status`}
                        >
                          {status.label}
                        </span>
                        {row.group_name && (
                          <span className="text-[10px] tracking-[0.18em] uppercase text-[#6A6863]">
                            {row.group_name}
                          </span>
                        )}
                        {row.status === "present" && row.arrived_at && (
                          <span className="text-[10px] tracking-[0.18em] uppercase text-[#6A6863]">
                            in {timeStr(row.arrived_at)} · {row.arrived_by_name}
                          </span>
                        )}
                        {row.status === "departed" && row.departed_at && (
                          <span className="text-[10px] tracking-[0.18em] uppercase text-[#6A6863]">
                            out {timeStr(row.departed_at)} · with {row.departed_to_name || "—"}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {row.status === "not_yet" && (
                        <button
                          type="button"
                          onClick={() => openSignIn(row)}
                          data-testid={`roster-row-${row.child_id}-sign-in`}
                          className="btn-primary !py-2 !px-4 !text-xs"
                        >
                          <LogIn size={12} strokeWidth={1.5} />
                          Sign in
                        </button>
                      )}
                      {row.status === "present" && (
                        <button
                          type="button"
                          onClick={() => openSignOut(row)}
                          data-testid={`roster-row-${row.child_id}-sign-out`}
                          className="btn-ghost !py-2 !px-4 !text-xs"
                        >
                          <LogOut size={12} strokeWidth={1.5} />
                          Sign out
                        </button>
                      )}
                      {row.status === "departed" && (
                        <span
                          data-testid={`roster-row-${row.child_id}-done`}
                          className="trust-pill"
                        >
                          <CheckCircle2 size={11} strokeWidth={1.5} />
                          Closed
                        </span>
                      )}
                      {row.status === "absent_today" && (
                        <span
                          data-testid={`roster-row-${row.child_id}-resting`}
                          className="trust-pill"
                          style={{ background: "#F2E7CF", color: "#1A1B2E" }}
                        >
                          Resting
                        </span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </section>
      </main>

      {/* Sign-in modal */}
      {signInFor && (
        <div
          data-testid="roster-sign-in-modal"
          className="fixed inset-0 z-[60] bg-[#FBF9F6]/95 backdrop-blur-xl flex items-center justify-center px-6"
        >
          <div className="bg-white rounded-3xl border hairline p-7 max-w-md w-full fade-up">
            <span className="eyebrow">Sign in</span>
            <h3 className="mt-3 font-serif text-2xl text-[#2C2B29]">
              {signInFor.child_name}
            </h3>
            <p className="mt-2 text-xs text-[#6A6863]">
              {signInFor.group_name || ""}
            </p>
            <label className="block mt-5">
              <span className="text-[10px] tracking-[0.18em] uppercase text-[#6A6863]">
                Gentle note (optional)
              </span>
              <textarea
                value={signInNote}
                onChange={(e) => setSignInNote(e.target.value)}
                data-testid="roster-sign-in-note"
                placeholder="e.g. settled in calmly"
                rows={3}
                className="mt-2 w-full bg-[#F2EFEA] rounded-2xl px-4 py-3 text-sm outline-none focus:bg-white border hairline"
              />
            </label>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setSignInFor(null)}
                data-testid="roster-sign-in-cancel"
                className="btn-ghost !py-2 !px-4 !text-xs"
              >
                Not now
              </button>
              <button
                type="button"
                onClick={confirmSignIn}
                disabled={busy}
                data-testid="roster-sign-in-confirm"
                className="btn-primary !py-2 !px-4 !text-xs"
              >
                {busy ? "Signing in…" : "Sign in gently"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sign-out modal */}
      {signOutFor && (
        <div
          data-testid="roster-sign-out-modal"
          className="fixed inset-0 z-[60] bg-[#FBF9F6]/95 backdrop-blur-xl flex items-center justify-center px-6"
        >
          <div className="bg-white rounded-3xl border hairline p-7 max-w-lg w-full fade-up overflow-y-auto max-h-[90vh]">
            <span className="eyebrow">Sign out</span>
            <h3 className="mt-3 font-serif text-2xl text-[#2C2B29]">
              {signOutFor.child_name}
            </h3>
            <p className="mt-2 text-xs text-[#6A6863]">
              Confirm who is picking up — we record this for the family.
            </p>

            {pickupAuths.length > 0 && (
              <div data-testid="roster-pickup-suggestions" className="mt-5">
                <span className="text-[10px] tracking-[0.18em] uppercase text-[#6A6863]">
                  Trusted pickup people
                </span>
                <div className="mt-2 flex flex-wrap gap-2">
                  {pickupAuths.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => choosePickupAuth(a)}
                      data-testid={`roster-pickup-suggestion-${a.id}`}
                      aria-pressed={pickupAuthId === a.id}
                      className={
                        "px-3 py-1.5 rounded-full text-xs transition-colors " +
                        (pickupAuthId === a.id
                          ? "bg-[#132619] text-white"
                          : "bg-[#F2EFEA] border hairline text-[#6A6863] hover:text-[#2C2B29]")
                      }
                    >
                      {a.name}
                      {a.relation ? ` · ${a.relation.replace("_", " ")}` : ""}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <label className="block mt-5">
              <span className="text-[10px] tracking-[0.18em] uppercase text-[#6A6863]">
                Pickup person
              </span>
              <input
                type="text"
                value={pickupName}
                onChange={(e) => {
                  setPickupName(e.target.value);
                  setPickupAuthId("");
                }}
                data-testid="roster-pickup-name"
                placeholder="Full name"
                className="mt-2 w-full bg-[#F2EFEA] rounded-2xl px-4 py-3 text-sm outline-none focus:bg-white border hairline"
              />
            </label>

            <label className="block mt-4">
              <span className="text-[10px] tracking-[0.18em] uppercase text-[#6A6863]">
                Relation
              </span>
              <select
                value={pickupRel}
                onChange={(e) => setPickupRel(e.target.value)}
                data-testid="roster-pickup-relation"
                className="mt-2 w-full bg-[#F2EFEA] rounded-2xl px-4 py-3 text-sm outline-none focus:bg-white border hairline"
              >
                {RELATIONS.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="block mt-4">
              <span className="text-[10px] tracking-[0.18em] uppercase text-[#6A6863]">
                Gentle note (optional)
              </span>
              <textarea
                value={pickupNote}
                onChange={(e) => setPickupNote(e.target.value)}
                data-testid="roster-pickup-note"
                placeholder="e.g. happy and ready to head home"
                rows={2}
                className="mt-2 w-full bg-[#F2EFEA] rounded-2xl px-4 py-3 text-sm outline-none focus:bg-white border hairline"
              />
            </label>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setSignOutFor(null)}
                data-testid="roster-sign-out-cancel"
                className="btn-ghost !py-2 !px-4 !text-xs"
              >
                Not now
              </button>
              <button
                type="button"
                onClick={confirmSignOut}
                disabled={busy}
                data-testid="roster-sign-out-confirm"
                className="btn-primary !py-2 !px-4 !text-xs"
              >
                {busy ? "Signing out…" : "Sign out gently"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
